import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';

interface QaSystemBridgeProps {
	windowId: string;
	meta?: Record<string, unknown>;
}

const shellStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	height: '100%',
	minHeight: 0,
	background: 'linear-gradient(180deg, rgba(10,10,12,0.96), rgba(4,4,7,0.98))',
	color: 'var(--khal-text, #f4f4f5)',
	fontFamily: 'var(--font-sans, Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif)',
};

const headerStyle: CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 16,
	padding: '18px 22px',
	borderBottom: '1px solid rgba(255,255,255,0.08)',
	background: 'rgba(255,255,255,0.03)',
};

const titleStyle: CSSProperties = {
	fontSize: 18,
	fontWeight: 650,
	letterSpacing: '-0.02em',
};

const subtitleStyle: CSSProperties = {
	marginTop: 4,
	fontSize: 12,
	color: 'var(--khal-text-secondary, #a1a1aa)',
};

const buttonStyle: CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: 999,
	border: '1px solid rgba(255,255,255,0.14)',
	background: 'rgba(255,255,255,0.08)',
	color: 'inherit',
	fontSize: 12,
	fontWeight: 600,
	padding: '8px 13px',
	textDecoration: 'none',
	whiteSpace: 'nowrap',
};

const fallbackStyle: CSSProperties = {
	flex: 1,
	display: 'grid',
	placeItems: 'center',
	padding: 32,
};

const cardStyle: CSSProperties = {
	maxWidth: 720,
	border: '1px solid rgba(255,255,255,0.10)',
	borderRadius: 20,
	padding: 28,
	background: 'rgba(255,255,255,0.045)',
	boxShadow: '0 24px 80px rgba(0,0,0,0.36)',
};

const statusPillStyle: CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: 8,
	borderRadius: 999,
	padding: '6px 10px',
	background: 'rgba(250,204,21,0.10)',
	border: '1px solid rgba(250,204,21,0.22)',
	color: '#fde68a',
	fontSize: 12,
	fontWeight: 700,
	textTransform: 'uppercase',
	letterSpacing: '0.08em',
};

function normalizeServiceUrl(value: unknown): string | null {
	if (typeof value !== 'string') return null;
	const trimmed = value.trim();
	if (!trimmed) return null;
	try {
		const url = new URL(trimmed);
		if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
		return url.href.replace(/\/$/, '');
	} catch {
		return null;
	}
}

function resolveQaSystemUrl(meta?: Record<string, unknown>): string | null {
	return (
		normalizeServiceUrl(meta?.qaSystemUrl) ??
		normalizeServiceUrl(meta?.serviceUrl) ??
		normalizeServiceUrl(import.meta.env.VITE_QA_SYSTEM_URL)
	);
}

/**
 * Temporary native bridge for the Eugenia QA System.
 *
 * The kernel registry can already expose `qa-system`, but Nebulosa currently
 * has no `/apps/qa-system/bundle.mjs` artifact, so routing it through the
 * generic DynamicAppBundle loader only shows "bundle fetch failed: 404".
 * This built-in bridge keeps the launcher entry usable while the pack install
 * pipeline catches up: if a service URL is configured it embeds the standalone
 * QA dashboard; otherwise it opens to an explicit operational status panel
 * instead of a broken bundle-loader error.
 */
export function QaSystemBridge({ meta }: QaSystemBridgeProps) {
	const qaSystemUrl = useMemo(() => resolveQaSystemUrl(meta), [meta]);
	const [iframeFailed, setIframeFailed] = useState(false);

	if (qaSystemUrl && !iframeFailed) {
		return (
			<div style={shellStyle} data-testid="qa-system-bridge">
				<div style={headerStyle}>
					<div>
						<div style={titleStyle}>QA System</div>
						<div style={subtitleStyle}>Eugenia QA + metrics dashboard bridge</div>
					</div>
					<a href={qaSystemUrl} target="_blank" rel="noreferrer" style={buttonStyle}>
						Open standalone
					</a>
				</div>
				<iframe
					title="QA System"
					src={qaSystemUrl}
					style={{ flex: 1, width: '100%', border: 0, background: '#050505' }}
					onError={() => setIframeFailed(true)}
				/>
			</div>
		);
	}

	return (
		<div style={shellStyle} data-testid="qa-system-bridge">
			<div style={headerStyle}>
				<div>
					<div style={titleStyle}>QA System</div>
					<div style={subtitleStyle}>Eugenia QA + metrics dashboard</div>
				</div>
			</div>
			<div style={fallbackStyle}>
				<div style={cardStyle}>
					<div style={statusPillStyle}>Service bridge active</div>
					<h2 style={{ margin: '18px 0 10px', fontSize: 28, letterSpacing: '-0.04em' }}>
						QA System opens without the missing bundle.
					</h2>
					<p style={{ margin: 0, color: 'var(--khal-text-secondary, #a1a1aa)', lineHeight: 1.65 }}>
						Nebulosa is registering <code>qa-system</code>, but the kernel does not currently serve
						 <code>/apps/qa-system/bundle.mjs</code>. The desktop now treats QA System as a
						 built-in bridge, so users no longer land on the dynamic bundle 404. Configure
						 <code>VITE_QA_SYSTEM_URL</code> or pass <code>qaSystemUrl</code> in app metadata to embed the
						 standalone Eugenia dashboard here.
					</p>
					{qaSystemUrl && iframeFailed ? (
						<p style={{ margin: '16px 0 0', color: '#fca5a5', fontSize: 13 }}>
							The configured QA System URL could not be embedded. Use the standalone link from the service config.
						</p>
					) : null}
				</div>
			</div>
		</div>
	);
}
